import { motion as Motion, AnimatePresence } from "framer-motion";
import { Pencil, Trash2, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { GetImageUrl } from "../../helpers/GetImageUrl";
import { http } from "../../axios/axios";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";
import { FormSelect } from "../../helpers/AdminHelper";
import AdminContentSectionEditor, { createPageSection } from "../../component/AdminContentSectionEditor";
import {
  focusFirstInvalidField,
  serializeSections,
  validatePageAdminForm,
  validationSummary,
} from "../../helpers/pageAdminValidation";

const AdminSolution = () => {
  const bannerInputRef = useRef(null);

  const sectionInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const [editId, setEditId] = useState(null);

  const [solutions, setSolutions] = useState([]);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [videoLinks, setVideoLinks] = useState([]);
  const [videoUrlInput, setVideoUrlInput] = useState("");
  const [existingGallery, setExistingGallery] = useState([]);
  const [existingVideos, setExistingVideos] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [faq, setFaq] = useState([{ ques: "", ans: "" }]);

  const [bannerAlt, setBannerAlt] = useState("");

  const [bannerTitle, setBannerTitle] = useState("");

  const [bannerSubtitle, setBannerSubtitle] = useState("");

  const [sectionAlt, setSectionAlt] = useState("");
  const [introductionHeading, setIntroductionHeading] = useState("");
  const [introductionDescription, setIntroductionDescription] = useState("");

  const [selectedCategory, setSelectedCategory] = useState("");

  const [selectedBrand, setSelectedBrand] = useState("");

  const [slug, setSlug] = useState("");

  const [images, setImages] = useState({
    banner: {
      file: null,
      preview: "",
    },
    section: {
      file: null,
      preview: "",
    },
  });

  const [contents, setContents] = useState([
    {
      heading: "",
      subHeading: "",
      description: "",
    },
  ]);

  const [features, setFeatures] = useState([
    {
      heading: "",
      subHeading: "",
      description: "",
      items: [
        {
          key: "",
          value: "",
        },
      ],
    },
  ]);
  const [sections, setSections] = useState([createPageSection("content")]);

  const [keywordInput, setKeywordInput] = useState("");

  const [seo, setSeo] = useState({
    title: "",
    description: "",
    keywords: [],
  });

  const [schemaScript, setSchemaScript] = useState("");
  const [cta, setCta] = useState({ heading: "", description: "", label: "", url: "" });

  const [category, setCategory] = useState([]);

  const [brands, setBrands] = useState([]);

  const addContent = () => {
    setContents((prev) => [
      ...prev,
      {
        heading: "",
        subHeading: "",
        description: "",
      },
    ]);
  };

  const addFeature = () => {
    setFeatures((prev) => [
      ...prev,
      {
        heading: "",
        subHeading: "",
        description: "",
        items: [
          {
            key: "",
            value: "",
          },
        ],
      },
    ]);
  };

  const addFeatureItem = (featureIndex) => {
    setFeatures((prev) =>
      prev.map((feature, i) =>
        i === featureIndex
          ? {
            ...feature,
            items: [
              ...feature.items,
              {
                key: "",
                value: "",
              },
            ],
          }
          : feature,
      ),
    );
  };

  const handleImageChange = (e, type) => {
    const file = e.target.files[0];

    if (!file) return;

    setImages((prev) => {
      if (prev[type].preview) {
        URL.revokeObjectURL(prev[type].preview);
      }

      return {
        ...prev,
        [type]: {
          file,
          preview: URL.createObjectURL(file),
        },
      };
    });
  };

  const handleMediaChange = (e, type) => {
    const files = Array.from(e.target.files || []).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    if (type === "gallery") setGalleryFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const removeSelectedMedia = (type, index) => {
    const setter = type === "gallery" ? setGalleryFiles : setVideoLinks;
    setter((prev) => {
      const item = prev[index];
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((_, itemIndex) => itemIndex !== index);
    });
  };

  const addVideoUrl = () => {
    const url = videoUrlInput.trim();
    if (!url) {
      toast.error("Please enter a valid video URL");
      return;
    }

    setVideoLinks((prev) => [...prev, { url, title: "", description: "" }]);
    setVideoUrlInput("");
  };

  const removeVideoLink = (index) => {
    setVideoLinks((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const removeExistingMedia = async (type, item) => {
    if (!editId || !item?._id) return;
    try {
      await http.delete(`/solutions/${editId}/${type}/${item._id}`);
      const setter = type === "videos" ? setExistingVideos : setExistingGallery;
      setter((prev) => prev.filter((media) => media._id !== item._id));
      toast.success("Media removed");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to remove media");
    }
  };

  const removeImages = (type) => {
    setImages((prev) => {
      if (prev[type].preview) {
        URL.revokeObjectURL(prev[type].preview);
      }

      return {
        ...prev,
        [type]: {
          file: null,
          preview: "",
        },
      };
    });

    if (type === "banner") {
      if (bannerInputRef.current) {
        bannerInputRef.current.value = "";
      }
    }

    if (type === "section") {
      if (sectionInputRef.current) {
        sectionInputRef.current.value = "";
      }
    }
  };

  const removeContent = (index) => {
    setContents((prev) => prev.filter((_, i) => i !== index));
  };

  const removeFeature = (index) => {
    setFeatures((prev) => {
      if (prev.length === 1) {
        return [
          {
            heading: "",
            subHeading: "",
            description: "",
            items: [
              {
                key: "",
                value: "",
              },
            ],
          },
        ];
      }

      return prev.filter((_, i) => i !== index);
    });
  };

  const removeFeatureItem = (featureIndex, itemIndex) => {
    setFeatures((prev) =>
      prev.map((feature, i) =>
        i === featureIndex
          ? {
            ...feature,
            items:
              feature.items.length === 1
                ? [
                  {
                    key: "",
                  },
                ]
                : feature.items.filter((_, itemI) => itemI !== itemIndex),
          }
          : feature,
      ),
    );
  };
  const removeFaq = (index) => {
    setFaq((prev) => prev.filter((_, i) => i !== index));
  };

  const addFaq = () => {
    setFaq((prev) => [
      ...prev,
      {
        ques: "",
        ans: "",
      },
    ]);
  };

  const resetForm = () => {
    galleryFiles.forEach((item) => URL.revokeObjectURL(item.preview));
    setGalleryFiles([]);
    setVideoLinks([]);
    setVideoUrlInput("");
    setExistingGallery([]);
    setExistingVideos([]);
    setImages({
      banner: {
        file: null,
        preview: "",
      },
      section: {
        file: null,
        preview: "",
      },
    });
    setBannerAlt("");
    setSectionAlt("");
    setIntroductionHeading("");
    setIntroductionDescription("");
    setSlug("");
    setContents([
      {
        heading: "",
        subHeading: "",
        description: "",
      },
    ]);

    setFeatures([
      {
        heading: "",
        subHeading: "",
        description: "",
        items: [
          {
            key: "",
            value: "",
          },
        ],
      },
    ]);
    setSections([createPageSection("content")]);

    setFaq([
      {
        ques: "",
        ans: "",
      },
    ]);

    setKeywordInput("");

    setSeo({
      title: "",
      description: "",
      keywords: [],
    });
    setSchemaScript("");
    setCta({ heading: "", description: "", label: "", url: "" });
    setBannerTitle("");
    setBannerSubtitle("");
    setSelectedCategory("");
    setSelectedBrand("");
    setErrors({});
    setIsSubmitting(false);
    setEditId(null);
    setIsModalOpen(false);
  };

  const fetchBrands = async () => {
    try {
      const res = await http.get("brands");
      setBrands(res.data.brands || []);
    } catch (err) {
      console.error("Unable to load brands:", err);
      toast.error("Failed to load brands");
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await http.get("category");

      setCategory(res.data.category || res.data.data || []);
    } catch (err) {
      console.error("Unable to load categories:", err);
      toast.error("Failed to load categories");
    }
  };

  const fetchSolutions = async () => {
    try {
      const res = await http.get("solutions");
      setSolutions(res.data?.data || []);
    } catch (err) {
      console.error("Unable to load solutions:", err);
      toast.error("Failed to load solutions");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const validation = validatePageAdminForm({
      images,
      bannerTitle,
      bannerSubtitle,
      slug,
      seo,
      faq,
      contents,
      features,
      sections,
      selectedCategory,
      selectedBrand,
      requireCatalogFields: true,
    });

    setErrors(validation.errors);
    if (Object.keys(validation.errors).length) {
      toast.error(validationSummary(validation.errors));
      focusFirstInvalidField();
      return;
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData();

      if (images.banner.file) {
        formData.append("bannerImage", images.banner.file);
      }

      if (images.section.file) {
        formData.append("sectionImage", images.section.file);
      }
      galleryFiles.forEach((item) => formData.append("galleryImages", item.file));
      if (videoLinks.length) {
        formData.append(
          "videos",
          JSON.stringify(
            videoLinks.map((item) => ({
              url: item.url,
              title: item.title || "",
              description: item.description || "",
              thumbnail: "",
            })),
          ),
        );
      }
      formData.append("galleryImagesMeta", JSON.stringify(existingGallery.map(({ _id, alt, title }) => ({ id: _id, alt, title }))));
      formData.append("videosMeta", JSON.stringify(existingVideos.map(({ _id, title, description }) => ({ id: _id, title, description }))));
      formData.append("bannerAlt", bannerAlt);
      formData.append("bannerTitle", bannerTitle);
      formData.append("bannerSubtitle", bannerSubtitle);
      formData.append("slug", slug.trim());
      formData.append("sectionAlt", sectionAlt);
      formData.append("introductionHeading", introductionHeading);
      formData.append("introductionDescription", introductionDescription);
      const contentPayload = validation.cleaned.contents.map((item, index) => {
        if (item.imageFile) formData.append(`contentImage_${index}`, item.imageFile);
        return {
          heading: item.heading,
          subHeading: item.subHeading,
          description: item.description,
          mediaType: item.mediaType || "",
          image: item.image ? { url: item.image.url || "", publicId: item.image.publicId || "", alt: item.image.alt || "" } : undefined,
          video: item.video ? { url: item.video.url || "", publicId: item.video.publicId || "", title: item.video.title || "", description: item.video.description || "", thumbnail: item.video.thumbnail || "" } : undefined,
        };
      });
      formData.append("contents", JSON.stringify(contentPayload));
      validation.cleaned.sections.forEach((section, index) => {
        if (section.imageFile) formData.append(`sectionMediaImage_${index}`, section.imageFile);
      });
      formData.append("sections", JSON.stringify(serializeSections(validation.cleaned.sections)));

      formData.append("features", JSON.stringify(validation.cleaned.features));

      formData.append("faq", JSON.stringify(validation.cleaned.faq));

      formData.append("seo", JSON.stringify(seo));
      formData.append("cta", JSON.stringify(cta));

      let schemaData = "";
      if (schemaScript?.trim()) {
        try {
          JSON.parse(schemaScript);
          schemaData = schemaScript;
        } catch {
          toast.error("Invalid Schema JSON");
          return;
        }
      }
      formData.append("schemaScript", schemaData);

      formData.append("category", selectedCategory);

      formData.append("brand", selectedBrand);

      let res;

      if (editId) {
        res = await http.put(`/solutions/update/${editId}`, formData);
      } else {
        res = await http.post("/solutions", formData);
      }

      if (editId) {
        setSolutions((prev) =>
          prev.map((item) => (item._id === editId ? res.data?.data : item)),
        );

        toast.success("Solution Updated");
      } else {
        setSolutions((prev) => [...prev, res.data?.data]);

        toast.success("Solution Created");
      }

      setEditId(null);
      resetForm();
    } catch (error) {
      const backendErrors = error.response?.data?.errors;
      if (backendErrors) {
        setErrors(backendErrors);
        focusFirstInvalidField();
      }
      toast.error(error.response?.data?.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (solutionId) => {
    try {
      if (!solutionId) return;

      const confirmDelete = window.confirm("Delete this solution?");

      if (!confirmDelete) return;

      await http.delete(`solutions/delete/${solutionId}`);

      setSolutions((prev) => prev.filter((item) => item._id !== solutionId));

      toast.success("Solution deleted");
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong");
    }
  };

  const handleEdit = async (solution) => {
    await setEditId(solution._id);
    setBannerTitle(solution.bannerTitle || "");
    setBannerSubtitle(solution.bannerSubtitle || "");
    setSlug(solution.slug || "");
    setContents(
      solution?.contents?.length
        ? solution?.contents
        : [
          {
            heading: "",
            subHeading: "",
            description: "",
          },
        ],
    );

    setFeatures(
      solution?.features?.length
        ? solution?.features
        : [
          {
            heading: "",
            subHeading: "",
            description: "",
            items: [
              {
                key: "",
                value: "",
              },
            ],
          },
        ],
    );
    setSections(
      solution?.sections?.length
        ? solution.sections
        : [
          ...(solution?.contents || []).map((content) => ({ ...createPageSection("content"), ...content, cards: content.cards || [] })),
          ...(solution?.features || []).map((feature) => ({ ...createPageSection("features"), heading: feature.heading, subHeading: feature.subHeading, description: feature.description, cards: (feature.items || []).map((item) => ({ title: item.key, description: item.value, key: item.key, value: item.value })) })),
        ],
    );

    setFaq(
      solution?.faq?.length
        ? solution?.faq
        : [
          {
            ques: "",
            ans: "",
          },
        ],
    );

    setSeo(
      solution?.seo || {
        title: "",
        description: "",
        keywords: [],
      },
    );
    setSchemaScript(solution.schemaScript || "");
    setCta(solution.cta || { heading: "", description: "", label: "", url: "" });
    setKeywordInput(solution.seo?.keywords?.join(", ") || "");
    setSelectedCategory(solution.category?._id || solution.category || "");
    setBannerAlt(solution.bannerAlt || "");
    setSectionAlt(solution.sectionAlt || "");
    setIntroductionHeading(solution.introductionHeading || "");
    setIntroductionDescription(solution.introductionDescription || "");
    setSelectedBrand(solution.brand?._id || solution.brand || "");

    setImages({
      banner: {
        file: null,
        preview: GetImageUrl(solution.bannerImage),
      },
      section: {
        file: null,
        preview: GetImageUrl(solution.sectionImage),
      },
    });
    setExistingGallery(solution.galleryImages || []);
    setExistingVideos(solution.videos || []);

    setIsModalOpen(true);
  };

  useEffect(() => {
    fetchBrands();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchSolutions();
  }, []);

  useEffect(() => {
    return () => {
      Object.values(images).forEach((item) => {
        if (item.preview) {
          URL.revokeObjectURL(item.preview);
        }
      });
    };
  }, []);

  useEffect(() => {
    if (editId && category.length) {
      const current = solutions.find((item) => item._id === editId);

      if (current) {
        setSelectedCategory(current.category?._id || current.category || "");
      }
    }
  }, [category, editId]);

  useEffect(() => {
    if (editId && brands.length) {
      const current = solutions.find((item) => item._id === editId);

      if (current) {
        setSelectedBrand(current.brand?._id || current.brand || "");
      }
    }
  }, [brands, editId]);

  return (
    <div className="p-4 md:p-8 min-h-screen text-slate-200">
      <AdminHeader
        title="Solutions"
        buttonText="Create Solution"
        onClick={() => {
          setEditId(null);
          resetForm();
          setKeywordInput("");
          setIsModalOpen(true);
        }}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-2">
        {solutions.map((solution) => (
          <div
            key={solution._id}
            className="
    group
    h-40
    bg-slate-900
    border
    border-blue-600
    rounded-2xl
    overflow-hidden
    relative
    shadow-lg
    hover:shadow-blue-500/20
    hover:-translate-y-1
    transition-all
    duration-300
  "
          >
            <img
              src={GetImageUrl(solution?.bannerImage)}
              loading="lazy"
              alt="solution"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />

            <div className="absolute inset-0 bg-black/20" />

            <div className="absolute bottom-0 left-0 w-full z-40 bg-black/70 p-3">
              <h3 className="text-white text-center font-semibold text-sm truncate">
                {solution?.bannerTitle}
              </h3>
            </div>
            <button
              onClick={() => handleEdit(solution)}
              className="
 absolute top-3 left-3
 z-50
 bg-blue-600
 p-2
 rounded-full
 "
            >
              <Pencil size={18} />
            </button>
            <button
              onClick={() => handleDelete(solution._id)}
              className="absolute top-3 right-3 z-50 bg-black/50 p-2 rounded-full"
            >
              <Trash2 size={18} className="text-red-400 hover:text-white" />
            </button>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <CreateSolutionModal
            images={images}
            handleSubmit={handleSubmit}
            handleImageChange={handleImageChange}
            closeModal={resetForm}
            removeImages={removeImages}
            setContents={setContents}
            addFeature={addFeature}
            addContent={addContent}
            contents={contents}
            features={features}
            setFeatures={setFeatures}
            addFeatureItem={addFeatureItem}
            faq={faq}
            setFaq={setFaq}
            addFaq={addFaq}
            removeContent={removeContent}
            removeFeature={removeFeature}
            removeFeatureItem={removeFeatureItem}
            removeFaq={removeFaq}
            setBannerTitle={setBannerTitle}
            setBannerSubtitle={setBannerSubtitle}
            seo={seo}
            setSeo={setSeo}
            keywordInput={keywordInput}
            setKeywordInput={setKeywordInput}
            setBrands={setBrands}
            brands={brands}
            setCategory={setCategory}
            category={category}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            selectedBrand={selectedBrand}
            setSelectedBrand={setSelectedBrand}
            bannerInputRef={bannerInputRef}
            sectionInputRef={sectionInputRef}
            bannerTitle={bannerTitle}
            bannerSubtitle={bannerSubtitle}
            bannerAlt={bannerAlt}
            setBannerAlt={setBannerAlt}
            schemaScript={schemaScript}
            setSchemaScript={setSchemaScript}
            cta={cta}
            setCta={setCta}
            sectionAlt={sectionAlt}
            slug={slug}
            setSlug={setSlug}
            setSectionAlt={setSectionAlt}
            introductionHeading={introductionHeading}
            setIntroductionHeading={setIntroductionHeading}
            introductionDescription={introductionDescription}
            setIntroductionDescription={setIntroductionDescription}
            sections={sections}
            setSections={setSections}
            galleryFiles={galleryFiles}
            videoLinks={videoLinks}
            videoUrlInput={videoUrlInput}
            setVideoUrlInput={setVideoUrlInput}
            setVideoLinks={setVideoLinks}
            addVideoUrl={addVideoUrl}
            removeVideoLink={removeVideoLink}
            handleMediaChange={handleMediaChange}
            removeSelectedMedia={removeSelectedMedia}
            existingGallery={existingGallery}
            setExistingGallery={setExistingGallery}
            existingVideos={existingVideos}
            setExistingVideos={setExistingVideos}
            removeExistingMedia={removeExistingMedia}
            galleryInputRef={galleryInputRef}
            videoInputRef={videoInputRef}
            errors={errors}
            isSubmitting={isSubmitting}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const CreateSolutionModal = ({
  bannerAlt,
  setBannerAlt,
  closeModal,
  handleSubmit,
  images,
  handleImageChange,
  removeImages,
  addContent,
  addFeature,
  setContents,
  setFeatures,
  contents,
  features,
  addFeatureItem,
  faq,
  setFaq,
  addFaq,
  removeContent,
  removeFeature,
  removeFeatureItem,
  removeFaq,
  bannerTitle,
  bannerSubtitle,
  setBannerSubtitle,
  setBannerTitle,
  seo,
  setSeo,
  keywordInput,
  setKeywordInput,
  setBrands,
  brands,
  setCategory,
  category,
  selectedCategory,
  setSelectedCategory,
  selectedBrand,
  setSelectedBrand,
  bannerInputRef,
  sectionInputRef,
  schemaScript,
  setSchemaScript,
  cta,
  setCta,
  sectionAlt,
  setSectionAlt,
  introductionHeading,
  setIntroductionHeading,
  introductionDescription,
  setIntroductionDescription,
  sections,
  setSections,
  slug,
  setSlug,
  galleryFiles,
  videoLinks,
  videoUrlInput,
  setVideoUrlInput,
  setVideoLinks,
  addVideoUrl,
  removeVideoLink,
  handleMediaChange,
  removeSelectedMedia,
  existingGallery,
  setExistingGallery,
  existingVideos,
  setExistingVideos,
  removeExistingMedia,
  galleryInputRef,
  videoInputRef,
  errors,
  isSubmitting,
}) => {
  const inputClass =
    "w-full border border-white/10 bg-white/5 text-white px-3 py-2 rounded-xl";

  const buttonClass = "bg-blue-600 px-2 py-1 rounded";

  return (
    <>
      <Motion.div className="fixed inset-0 bg-black/90 backdrop-blur-xl z-50 ">
        <Motion.div
          className="
    fixed z-50 
    top-1/2 left-1/2 
    -translate-x-1/2 
    -translate-y-1/2
    w-[92%]
    max-h-[90vh]
    bg-[#0a0f1a]/95
    border border-white/10
    rounded
    shadow-2xl
    overflow-hidden
    flex flex-col
  "
        >
          <div className="px-8 py-6 flex items-center justify-between bg-white/3 w-full ">
            <div>
              <h3 className="text-lg font-black text-white uppercase">
                Solution Creation
              </h3>
            </div>
            <button
              type="button"
              onClick={closeModal}
              className="
            h-10 w-10
            rounded-xl
            bg-white/5
            hover:bg-red-500
            text-slate-400
            hover:text-white
            transition-all
            flex items-center justify-center
          "
            >
              <X size={20} />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 gap-4 p-8 overflow-y-auto flex-1"
          >
            {Object.keys(errors).length > 0 && (
              <div className="rounded-lg border border-red-500/50 bg-red-500/10 p-4 text-sm text-red-300">
                {validationSummary(errors)}
              </div>
            )}
            <div data-error={Boolean(errors.bannerImage)}>
              <label className="text-sm font-semibold text-slate-200">1. Hero Section - Banner Image <span className="text-red-500">*</span></label>

              <input
                ref={bannerInputRef}
                type="file"
                name="banner.image"
                accept="image/*"
                onChange={(e) => handleImageChange(e, "banner")}
                className={`${inputClass} ${errors.bannerImage ? "border-red-500" : ""}`}
              />
              <FieldError message={errors.bannerImage} />
              {images?.banner?.preview && (
                <div className="relative w-30 h-30 mt-3">
                  <img
                    src={images?.banner?.preview}
                    className="w-full h-full object-cover rounded"
                  />

                  <button
                    type="button"
                    onClick={() => removeImages("banner")}
                    className="
        absolute
        -top-2
        -right-2
        bg-red-600
        text-white
        rounded-full
        w-6
        h-6
        flex
        items-center
        justify-center
      "
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm text-slate-300">
                Banner Image Alt Text
              </label>

              <input
                type="text"
                value={bannerAlt}
                onChange={(e) => setBannerAlt(e.target.value)}
                placeholder="Enter banner image alt text"
                className={inputClass}
              />
            </div>
            <div data-error={Boolean(errors.bannerTitle)}>
              <label className="text-sm text-slate-300">Banner Title <span className="text-red-500">*</span></label>

              <input
                type="text"
                name="banner.title"
                value={bannerTitle}
                onChange={(e) => setBannerTitle(e.target.value)}
                placeholder="Smarter Access Control for Secure Workplaces"
                className={`${inputClass} ${errors.bannerTitle ? "border-red-500" : ""}`}
              />
              <FieldError message={errors.bannerTitle} />
            </div>
            <div data-error={Boolean(errors.bannerSubtitle)}>
              <label className="text-sm text-slate-300">Banner Subtitle <span className="text-red-500">*</span></label>

              <textarea
                name="banner.subtitle"
                value={bannerSubtitle}
                onChange={(e) => setBannerSubtitle(e.target.value)}
                placeholder="Managing who enters your premises..."
                rows={4}
                className={`${inputClass} ${errors.bannerSubtitle ? "border-red-500" : ""}`}
              />
              <FieldError message={errors.bannerSubtitle} />
            </div>
            
            <div data-error={Boolean(errors.category)}>
              <FormSelect
                label="Category *"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                placeholder="Select Category"
                options={category.map((item) => ({
                  value: item?._id,
                  label: item?.name,
                }))}
              />
              <FieldError message={errors.category} />
            </div>

            <div data-error={Boolean(errors.brand)}>
              <FormSelect
                label="Brand *"
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                placeholder="Select Brand"
                options={brands.map((item) => ({
                  value: item?._id,
                  label: item?.brandName || item?.name,
                }))}
              />
              <FieldError message={errors.brand} />
            </div>

            <div className="order-6 space-y-4">
              <h4 className="text-sm font-semibold text-white">Gallery Images</h4>
              <input ref={galleryInputRef} type="file" accept="image/*" multiple onChange={(e) => handleMediaChange(e, "gallery")} className={inputClass} />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {existingGallery.map((item) => (
                  <div key={item._id || item.publicId || item.url} className="relative">
                    <img src={GetImageUrl(item.url)} alt={item.alt || "Gallery"} className="h-24 w-full rounded object-cover" loading="lazy" />
                    <input value={item.alt || ""} onChange={(e) => setExistingGallery((prev) => prev.map((media) => mediaIdentity(media) === mediaIdentity(item) ? { ...media, alt: e.target.value } : media))} placeholder="Image alt text" className="mt-1 w-full rounded bg-white/10 px-2 py-1 text-xs text-white" />
                    <button type="button" onClick={() => removeExistingMedia("galleryImages", item)} className="absolute right-1 top-1 rounded-full bg-red-600 p-1"><X size={12} /></button>
                  </div>
                ))}
                {galleryFiles.map((item, index) => (
                  <div key={`${item.file.name}-${index}`} className="relative"><img src={item.preview} alt="Selected gallery" className="h-24 w-full rounded object-cover" /><button type="button" onClick={() => removeSelectedMedia("gallery", index)} className="absolute right-1 top-1 rounded-full bg-red-600 p-1"><X size={12} /></button></div>
                ))}
              </div>
            </div>
            <div className="order-5 space-y-4">
              <h4 className="text-sm font-semibold text-white">Videos</h4>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={videoUrlInput}
                  onChange={(e) => setVideoUrlInput(e.target.value)}
                  placeholder="https://example.com/video.mp4"
                  className={`${inputClass} flex-1`}
                />
                <button type="button" onClick={addVideoUrl} className="rounded bg-cyan-700 px-3 py-2 text-sm font-semibold text-white">Add URL</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {existingVideos.map((item) => (
                  <div key={item._id || item.publicId || item.url} className="relative"><video src={item.url} controls preload="metadata" className="w-full rounded" /><input value={item.title || ""} onChange={(e) => setExistingVideos((prev) => prev.map((media) => mediaIdentity(media) === mediaIdentity(item) ? { ...media, title: e.target.value } : media))} placeholder="Video title" className="mt-1 w-full rounded bg-white/10 px-2 py-1 text-xs text-white" /><textarea value={item.description || ""} onChange={(e) => setExistingVideos((prev) => prev.map((media) => mediaIdentity(media) === mediaIdentity(item) ? { ...media, description: e.target.value } : media))} placeholder="Video caption" className="mt-1 w-full rounded bg-white/10 px-2 py-1 text-xs text-white" /><button type="button" onClick={() => removeExistingMedia("videos", item)} className="absolute right-1 top-1 rounded-full bg-red-600 p-1"><X size={12} /></button></div>
                ))}
                {videoLinks.map((item, index) => (
                  <div key={`${item.url}-${index}`} className="relative">
                    <video src={item.url} controls preload="metadata" className="w-full rounded" />
                    <input value={item.title || ""} onChange={(e) => setVideoLinks((prev) => prev.map((video, videoIndex) => videoIndex === index ? { ...video, title: e.target.value } : video))} placeholder="Video title" className="mt-1 w-full rounded bg-white/10 px-2 py-1 text-xs text-white" />
                    <textarea value={item.description || ""} onChange={(e) => setVideoLinks((prev) => prev.map((video, videoIndex) => videoIndex === index ? { ...video, description: e.target.value } : video))} placeholder="Video caption" className="mt-1 w-full rounded bg-white/10 px-2 py-1 text-xs text-white" />
                    <input value={item.url} onChange={(e) => setVideoLinks((prev) => prev.map((video, videoIndex) => videoIndex === index ? { ...video, url: e.target.value } : video))} placeholder="Video URL" className="mt-1 w-full rounded bg-white/10 px-2 py-1 text-xs text-white" />
                    <button type="button" onClick={() => removeVideoLink(index)} className="absolute right-1 top-1 rounded-full bg-red-600 p-1"><X size={12} /></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-2" data-error={Boolean(errors.sectionImage)}>
              <label className="text-sm font-semibold text-slate-200">2. Introduction Section - Image <span className="text-red-500">*</span></label>

              <input
                ref={sectionInputRef}
                type="file"
                name="section.image"
                accept="image/*"
                onChange={(e) => handleImageChange(e, "section")}
                className={`${inputClass} ${errors.sectionImage ? "border-red-500" : ""}`}
              />
              <FieldError message={errors.sectionImage} />
              {images?.section?.preview && (
                <div className="relative w-30 h-30 mt-3">
                  <img
                    src={images?.section?.preview}
                    className="w-full h-full object-cover rounded"
                  />

                  <button
                    type="button"
                    onClick={() => removeImages("section")}
                    className="
        absolute
        -top-2
        -right-2
        bg-red-600
        text-white
        rounded-full
        w-6
        h-6
        flex
        items-center
        justify-center
      "
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm text-slate-300">
                Section Image Alt Text
              </label>

              <input
                type="text"
                value={sectionAlt}
                onChange={(e) => setSectionAlt(e.target.value)}
                placeholder="Enter section image alt text"
                className={inputClass}
              />
              <input value={introductionHeading} onChange={(e) => setIntroductionHeading(e.target.value)} placeholder="Introduction heading" className={`${inputClass} mt-3`} />
              <textarea value={introductionDescription} onChange={(e) => setIntroductionDescription(e.target.value)} placeholder="Introduction description" rows={4} className={`${inputClass} mt-2`} />
            </div>
            <div className="order-3"><AdminContentSectionEditor sections={sections} setSections={setSections} inputClass={inputClass} /></div>
            <div className="hidden">
              <div className="flex justify-between items-center">
                <label className="text-sm text-slate-300">Type Feature</label>

                <button
                  type="button"
                  onClick={addFeature}
                  className={buttonClass}
                >
                  + Add Feature
                </button>
              </div>

              {features.map((feature, index) => (
                <div key={index} className="space-y-3 mt-4">
                  <input
                    placeholder="Feature Heading"
                    value={feature?.heading}
                    onChange={(e) => {
                      const copy = [...features];
                      copy[index].heading = e.target.value;
                      setFeatures(copy);
                    }}
                    className={inputClass}
                  />

                  <input
                    placeholder="Feature Sub Heading"
                    value={feature?.subHeading}
                    onChange={(e) => {
                      const copy = [...features];
                      copy[index].subHeading = e.target.value;
                      setFeatures(copy);
                    }}
                    className={inputClass}
                  />
                  <input
                    placeholder="Feature Description"
                    value={feature?.description}
                    onChange={(e) => {
                      const copy = [...features];
                      copy[index].description = e.target.value;
                      setFeatures(copy);
                    }}
                    className={inputClass}
                  />

                  {feature.items.map((item, itemIndex) => (
                    <div key={`${index}-${itemIndex}`} className="flex gap-2">
                      <input
                        placeholder="Key"
                        value={item?.key}
                        onChange={(e) => {
                          const copy = [...features];
                          copy[index].items[itemIndex].key = e.target.value;
                          setFeatures(copy);
                        }}
                        className={inputClass}
                      />

                      <input
                        placeholder="Value"
                        value={item?.value}
                        onChange={(e) => {
                          const copy = [...features];
                          copy[index].items[itemIndex].value = e.target.value;
                          setFeatures(copy);
                        }}
                        className={inputClass}
                      />
                      <button
                        type="button"
                        onClick={() => removeFeatureItem(index, itemIndex)}
                        className="bg-red-600 px-3 rounded"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center gap-5 ">
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="bg-red-600 px-3 py-1 rounded"
                    >
                      Remove Feature
                    </button>
                    <button
                      type="button"
                      onClick={() => addFeatureItem(index)}
                      className="bg-blue-600 px-3 py-1 rounded"
                    >
                      + Add Item
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="order-7">
              <div className="flex justify-between items-center">
                <label className="text-sm text-slate-300">FAQ</label>

                <button type="button" onClick={addFaq} className={buttonClass}>
                  + Add FAQ
                </button>
              </div>

              {faq.map((item, index) => (
                <div key={index} className="mt-4 space-y-2" data-error={Boolean(errors[`faq.${index}.ques`] || errors[`faq.${index}.ans`])}>
                  <input
                    placeholder="Question"
                    value={item?.ques}
                    onChange={(e) => {
                      const copy = [...faq];

                      copy[index].ques = e.target.value;

                      setFaq(copy);
                    }}
                    className={`${inputClass} ${errors[`faq.${index}.ques`] ? "border-red-500" : ""}`}
                  />
                  <FieldError message={errors[`faq.${index}.ques`]} />

                  <textarea
                    placeholder="Answer"
                    value={item?.ans}
                    onChange={(e) => {
                      const copy = [...faq];

                      copy[index].ans = e.target.value;

                      setFaq(copy);
                    }}
                    className={`${inputClass} ${errors[`faq.${index}.ans`] ? "border-red-500" : ""}`}
                  />
                  <FieldError message={errors[`faq.${index}.ans`]} />
                  <button
                    type="button"
                    onClick={() => removeFaq(index)}
                    className="bg-red-600 px-3 py-1 rounded"
                  >
                    Remove FAQ
                  </button>
                </div>
              ))}
            </div>
            <div className="order-8" data-error={Boolean(errors.seoTitle)}>
              <label className="text-sm text-slate-300">SEO Title <span className="text-red-500">*</span></label>
              <input
                value={seo?.title}
                onChange={(e) => setSeo({ ...seo, title: e.target.value })}
                placeholder="Access Control Solutions | Company Name"
                className={`${inputClass} ${errors.seoTitle ? "border-red-500" : ""}`}
              />
              <FieldError message={errors.seoTitle} />
            </div>
            <div data-error={Boolean(errors.slug)}>
              <label className="text-sm text-slate-300">Slug <span className="text-red-500">*</span></label>

              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="cctv-security-solution"
                className={`${inputClass} ${errors.slug ? "border-red-500" : ""}`}
              />
              <FieldError message={errors.slug} />
            </div>
            <div className="order-8" data-error={Boolean(errors.seoDescription)}>
              <label className="text-sm text-slate-300">SEO Description <span className="text-red-500">*</span></label>
              <textarea
                value={seo?.description}
                onChange={(e) =>
                  setSeo({ ...seo, description: e.target.value })
                }
                placeholder="Secure workplace access control solutions..."
                className={`${inputClass} ${errors.seoDescription ? "border-red-500" : ""}`}
              />
              <FieldError message={errors.seoDescription} />
            </div>
            <div className="order-8" data-error={Boolean(errors.seoKeywords)}>
              <label className="text-sm text-slate-300">SEO Keywords <span className="text-red-500">*</span></label>
              <input
                value={keywordInput}
                onChange={(e) => {
                  setKeywordInput(e.target.value);

                  setSeo({
                    ...seo,
                    keywords: e.target.value
                      .split(",")
                      .map((item) => item.trim())
                      .filter(Boolean),
                  });
                }}
                placeholder="access control, security, workplace"
                className={`${inputClass} ${errors.seoKeywords ? "border-red-500" : ""}`}
              />
              <FieldError message={errors.seoKeywords} />
            </div>
            <div className="order-8">
              <label className="text-sm text-slate-300">
                Schema Script (JSON-LD)
              </label>

              <textarea
                rows={8}
                value={schemaScript || ""}
                onChange={(e) => setSchemaScript(e.target.value)}
                placeholder={`{
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "CCTV Solution"
}`}
                className={inputClass}
              />

              <p className="text-xs text-slate-400 mt-2">
                Add valid JSON-LD schema only
              </p>
            </div>
            <div className="order-8 grid gap-2"><label className="text-sm font-semibold text-slate-200">8. CTA</label><input value={cta.heading} onChange={(e) => setCta({ ...cta, heading: e.target.value })} placeholder="CTA heading" className={inputClass} /><textarea value={cta.description} onChange={(e) => setCta({ ...cta, description: e.target.value })} placeholder="CTA description" rows={2} className={inputClass} /><div className="grid gap-2 sm:grid-cols-2"><input value={cta.label} onChange={(e) => setCta({ ...cta, label: e.target.value })} placeholder="CTA button label" className={inputClass} /><input value={cta.url} onChange={(e) => setCta({ ...cta, url: e.target.value })} placeholder="CTA URL" className={inputClass} /></div></div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="order-last bg-blue-600 py-3 rounded-xl text-white mt-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? "Saving..." : "Save Solution"}
            </button>
          </form>
        </Motion.div>
      </Motion.div>
    </>
  );
};

const FieldError = ({ message }) =>
  message ? <p className="mt-1 text-sm text-red-500">{message}</p> : null;

const mediaIdentity = (item) => item?._id || item?.publicId || item?.url;

export default AdminSolution;


